import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { trackEvent } from "@/lib/analytics";
import {
  Upload as UploadIcon,
  FileAudio,
  Activity,
  Play,
  Zap,
  Quote,
  LayoutList,
  Star,
  Check,
  ArrowRight,
  Rss,
  Search,
  Loader2,
  AlertCircle,
  CheckSquare,
  Square,
  Calendar,
  Clock,
  Info,
  BellPlus,
  ExternalLink,
  X,
  ChevronDown,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useRecentFeeds, readRecentFeeds } from "@/hooks/use-recent-feeds";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListJobsQueryKey,
  getRssEpisodes,
  createRssJob,
  createSubscription,
  backfillSubscriptionByUrls,
} from "@workspace/api-client-react";
import type { RssEpisode } from "@workspace/api-client-react";

type Mode = "quick_catchup" | "key_quotes" | "full_recap";
type InputMode = "file" | "rss";

const ALLOWED_MIME_TYPES = new Set([
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/wave",
  "audio/x-wav",
  "audio/mp4",
  "audio/x-m4a",
  "audio/m4a",
  "audio/aac",
  "audio/x-aac",
  "audio/ogg",
  "audio/vorbis",
  "audio/flac",
  "audio/x-flac",
]);

const ALLOWED_AUDIO_EXTENSIONS = new Set([
  "mp3",
  "wav",
  "wave",
  "mp4",
  "m4a",
  "aac",
  "ogg",
  "flac",
]);

const AMBIGUOUS_MIME_TYPES = new Set([
  "",
  "application/octet-stream",
]);

function hasAudioExtension(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ALLOWED_AUDIO_EXTENSIONS.has(ext);
}

function isAudioFile(file: File): boolean {
  if (AMBIGUOUS_MIME_TYPES.has(file.type)) {
    return hasAudioExtension(file);
  }
  return ALLOWED_MIME_TYPES.has(file.type) || file.type.startsWith("audio/");
}

function formatMinutes(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s}s`;
  return s === 0 ? `${m} min` : `${m}m ${s}s`;
}

function formatPubDate(dateStr: string | null): string {
  if (!dateStr) return "Unknown date";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

interface ModeCardProps {
  id: Mode;
  label: string;
  description: string;
  icon: React.ReactNode;
  targetLabel: string;
  estimatedOutput: string | null;
  featured?: boolean;
  selected: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}

function ModeCard({
  id,
  label,
  description,
  icon,
  targetLabel,
  estimatedOutput,
  featured,
  selected,
  onSelect,
  children,
}: ModeCardProps) {
  return (
    <div
      data-testid={`mode-card-${id}`}
      role="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={[
        "relative rounded-lg border-2 p-5 cursor-pointer transition-all duration-200 select-none",
        selected
          ? "border-primary bg-primary/10"
          : featured
            ? "border-primary/40 bg-card hover:border-primary/70 hover:bg-primary/5"
            : "border-border/50 bg-card hover:border-primary/40 hover:bg-secondary/20",
      ].join(" ")}
    >
      {featured && (
        <div className="absolute -top-3 left-4 flex items-center gap-1 bg-primary text-primary-foreground text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-full">
          <Star size={10} className="fill-current" /> Featured
        </div>
      )}
      <div className="flex items-start gap-4">
        <div
          className={[
            "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
            selected ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground",
          ].join(" ")}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-mono font-bold text-sm uppercase tracking-wider">{label}</h3>
            <div
              className={[
                "w-4 h-4 rounded-full border-2 flex-shrink-0 transition-colors",
                selected ? "border-primary bg-primary" : "border-border",
              ].join(" ")}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
          <div className="flex items-center gap-4 mt-3">
            <span className="text-xs font-mono text-muted-foreground">
              Target: <span className="text-primary">{targetLabel}</span>
            </span>
            {estimatedOutput && (
              <span className="text-xs font-mono text-muted-foreground">
                Est. output: <span className="text-primary font-medium">{estimatedOutput}</span>
              </span>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function UploadPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { getToken, isSignedIn } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { recentFeeds, addRecentFeed, removeRecentFeed } = useRecentFeeds();
  const [showRecentFeeds, setShowRecentFeeds] = useState(false);

  const [inputMode, setInputMode] = useState<InputMode>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("tab") === "rss" ? "rss" : "file";
    } catch {
      return "file";
    }
  });

  // File upload state
  const [file, setFile] = useState<File | null>(null);
  const [fileDurationSec, setFileDurationSec] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // RSS state — pre-fill with the most recently used feed URL
  const [rssUrl, setRssUrl] = useState(() => readRecentFeeds()[0] ?? "");
  const [rssFeedTitle, setRssFeedTitle] = useState<string | null>(null);
  const [rssEpisodes, setRssEpisodes] = useState<RssEpisode[] | null>(null);
  const [rssFetching, setRssFetching] = useState(false);
  const [rssError, setRssError] = useState<string | null>(null);
  const [selectedEpisodeGuids, setSelectedEpisodeGuids] = useState<Set<string>>(new Set());
  const [isSubmittingRss, setIsSubmittingRss] = useState(false);

  // Subscription state
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribeSelectedUrls, setSubscribeSelectedUrls] = useState<Set<string>>(new Set());

  // User quota state (fetched for RSS tab quota gating)
  const [userPlan, setUserPlan] = useState<"free" | "pro" | null>(null);
  const [userUsageCount, setUserUsageCount] = useState<number | null>(null);
  const [userAudioSecondsUsed, setUserAudioSecondsUsed] = useState<number | null>(null);
  const FREE_LIMIT = 5;
  const PRO_AUDIO_SECONDS_LIMIT = 50 * 60 * 60;

  // Shared state
  const [modes, setModes] = useState<Set<Mode>>(() => new Set<Mode>(["key_quotes"]));
  const [fullRecapPercent, setFullRecapPercent] = useState<number>(50);
  const [fastMode, setFastMode] = useState(false);

  const toggleMode = (m: Mode) => {
    if (isUploading || isSubmittingRss) return;
    setModes((prev) => {
      const next = new Set(prev);
      if (next.has(m)) {
        if (next.size > 1) next.delete(m);
      } else {
        next.add(m);
      }
      return next;
    });
  };
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Fetch user quota when RSS tab is opened
  const fetchUserQuota = useCallback(async () => {
    try {
      const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await fetch(`${basePath}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json() as { plan: string; usageCount: number; audioSecondsUsed?: number };
        setUserPlan(data.plan === "pro" ? "pro" : "free");
        setUserUsageCount(data.usageCount ?? 0);
        setUserAudioSecondsUsed(data.audioSecondsUsed ?? 0);
      }
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    if (inputMode === "rss") {
      fetchUserQuota();
    }
  }, [inputMode, fetchUserQuota]);

  // Auto-fetch on mount when the page opens directly on the RSS tab (e.g. ?tab=rss)
  // and a recent feed URL is already pre-filled.
  useEffect(() => {
    if (inputMode === "rss" && rssUrl.trim()) {
      handleFetchRss();
    }
    // Intentionally run only on mount — dependencies are stable initial values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Measure audio file duration in the browser when file is selected
  useEffect(() => {
    if (!file) {
      setFileDurationSec(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      setFileDurationSec(isFinite(audio.duration) ? audio.duration : null);
      URL.revokeObjectURL(url);
    };
    audio.onerror = () => {
      setFileDurationSec(null);
      URL.revokeObjectURL(url);
    };
    audio.src = url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  function estimatedOutputFromDuration(durSec: number, pct: number): string {
    return formatMinutes(durSec * (pct / 100));
  }

  function estimatedOutput(pct: number): string | null {
    if (!fileDurationSec) return null;
    return estimatedOutputFromDuration(fileDurationSec, pct);
  }

  const setValidFile = (candidate: File) => {
    if (!isAudioFile(candidate)) {
      toast({
        title: "Invalid file type",
        description: "Please select an audio file (MP3, WAV, M4A, AAC, OGG, or FLAC).",
        variant: "destructive",
      });
      return;
    }
    setFile(candidate);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setValidFile(selected);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploading) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setValidFile(dropped);
  };

  // Keep chunks small so they pass through the production reverse-proxy comfortably
  const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk

  /**
   * Upload a large file by splitting it into ≤5 MB chunks, sending each
   * chunk through the API server.  Returns the uploadId and totalChunks so
   * the caller can call the finalize endpoint (once, for all selected modes).
   */
  async function uploadChunksOnly(
    audioFile: File,
    basePath: string,
    token: string | null,
  ): Promise<{ uploadId: string; totalChunks: number }> {
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(audioFile.size / CHUNK_SIZE);
    let bytesUploaded = 0;

    const MAX_CHUNK_ATTEMPTS = 3;

    class PermanentUploadError extends Error {
      constructor(message: string) {
        super(message);
        this.name = "PermanentUploadError";
      }
    }

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, audioFile.size);
      const chunk = audioFile.slice(start, end);
      let lastError: Error | null = null;

      for (let attempt = 0; attempt < MAX_CHUNK_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await new Promise<void>((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
          setProgress(Math.round((bytesUploaded / audioFile.size) * 100));
          setRetryMessage(`Connection hiccup — retrying chunk ${i + 1} of ${totalChunks}…`);
        }

        const result = await new Promise<"ok" | Error>((resolve) => {
          const xhr = new XMLHttpRequest();
          const form = new FormData();
          form.append("uploadId", uploadId);
          form.append("chunkIndex", String(i));
          form.append("totalChunks", String(totalChunks));
          form.append("chunk", chunk, audioFile.name);

          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const chunkProgress = bytesUploaded + event.loaded;
              setProgress(Math.round((chunkProgress / audioFile.size) * 100));
            }
          });

          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              bytesUploaded = end;
              resolve("ok");
            } else if (xhr.status >= 500) {
              let message = `Chunk ${i + 1}/${totalChunks} failed (server error ${xhr.status}).`;
              try { message = (JSON.parse(xhr.responseText) as { error?: string }).error ?? message; } catch { /* ignore */ }
              resolve(new Error(message));
            } else {
              let message = `Chunk ${i + 1}/${totalChunks} failed to upload.`;
              try { message = (JSON.parse(xhr.responseText) as { error?: string }).error ?? message; } catch { /* ignore */ }
              resolve(new PermanentUploadError(message));
            }
          });
          xhr.addEventListener("error", () =>
            resolve(new Error(`Network error uploading chunk ${i + 1}/${totalChunks}.`)),
          );

          xhr.open("POST", `${basePath}/api/jobs/upload-chunk`);
          if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          xhr.send(form);
        });

        if (result === "ok") { lastError = null; setRetryMessage(null); break; }
        if (result instanceof PermanentUploadError) throw result;
        lastError = result as Error;
      }

      if (lastError) throw lastError;
    }

    return { uploadId, totalChunks };
  }

  /**
   * Finalize a chunked upload for one or more modes.  Returns a single job
   * {id} for a single mode, or {ids:[]} for multiple modes.
   */
  async function finalizeChunkedUpload(
    audioFile: File,
    basePath: string,
    token: string | null,
    uploadId: string,
    totalChunks: number,
    modesArr: Mode[],
    recapPct: number,
  ): Promise<{ id: number } | { ids: number[] } | null> {
    const finalizeRes = await fetch(`${basePath}/api/jobs/upload-finalize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({
        uploadId,
        originalName: audioFile.name,
        totalChunks,
        ...(modesArr.length === 1
          ? { mode: modesArr[0], ...(modesArr[0] === "full_recap" ? { targetPercent: recapPct } : {}) }
          : { modes: modesArr, ...(modesArr.includes("full_recap") ? { targetPercent: recapPct } : {}) }),
        ...(fastMode ? { fastMode: true } : {}),
      }),
    });

    if (finalizeRes.ok) return finalizeRes.json() as Promise<{ id: number } | { ids: number[] }>;

    let message = "There was an error finalizing your upload.";
    let code: string | undefined;
    try {
      const parsed = await finalizeRes.json() as { error?: string; code?: string };
      if (parsed.error) message = parsed.error;
      code = parsed.code;
    } catch { /* ignore */ }
    if (finalizeRes.status === 429 && code === "USAGE_LIMIT_EXCEEDED") {
      setShowUpgradePrompt(true);
      return null;
    }
    throw new Error(message);
  }

  /** Legacy single-mode chunked upload (chunks + finalize in one call). */
  async function uploadChunkedFallback(
    audioFile: File,
    basePath: string,
    token: string | null,
    jobMode: Mode,
    recapPct: number,
  ): Promise<{ id: number } | null> {
    const { uploadId, totalChunks } = await uploadChunksOnly(audioFile, basePath, token);
    const result = await finalizeChunkedUpload(audioFile, basePath, token, uploadId, totalChunks, [jobMode], recapPct);
    if (result === null) return null;
    return result as { id: number };
  }

  // Files at or below this size use a single multipart POST to the API.
  // Larger files are split into ≤5 MB chunks to stay under any proxy body limit.
  const DIRECT_UPLOAD_THRESHOLD = 10 * 1024 * 1024; // 10 MB

  const handleUpload = async () => {
    if (!file) return;
    if (!isSignedIn) { setLocation("/sign-up"); return; }

    setIsUploading(true);
    setProgress(0);
    setRetryMessage(null);

    const token = await getToken();
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

    try {
      const modesArr = [...modes];
      const MAX_SMALL_ATTEMPTS = 3;

      type SmallUploadResult =
        | { kind: "success"; data: { id: number } }
        | { kind: "upgrade" }
        | { kind: "retryable"; error: Error }
        | { kind: "permanent"; error: Error };

      /** Send one small-file POST for a given mode with retry logic. */
      const uploadSmallFile = async (jobMode: Mode): Promise<{ id: number } | null> => {
        let lastErr: Error | null = null;
        let result: { id: number } | null = null;

        for (let attempt = 0; attempt < MAX_SMALL_ATTEMPTS; attempt++) {
          if (attempt > 0) {
            await new Promise<void>((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
            setProgress(0);
            setRetryMessage(`Connection hiccup — retrying upload (attempt ${attempt + 1} of ${MAX_SMALL_ATTEMPTS})…`);
          }

          const outcome = await new Promise<SmallUploadResult>((resolve) => {
            const xhr = new XMLHttpRequest();
            const form = new FormData();
            form.append("audio", file, file.name);
            form.append("mode", jobMode);
            if (jobMode === "full_recap") form.append("targetPercent", String(fullRecapPercent));
            if (fastMode) form.append("fastMode", "true");

            xhr.upload.addEventListener("progress", (event) => {
              if (event.lengthComputable) setProgress(Math.round((event.loaded / event.total) * 100));
            });

            xhr.addEventListener("load", () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ kind: "success", data: JSON.parse(xhr.responseText) as { id: number } });
              } else if (xhr.status >= 500) {
                let message = `Upload failed (server error ${xhr.status}). Please try again.`;
                try { message = (JSON.parse(xhr.responseText) as { error?: string }).error ?? message; } catch { /* ignore */ }
                resolve({ kind: "retryable", error: new Error(message) });
              } else {
                let message = "Upload failed. Please try again.";
                let code: string | undefined;
                try {
                  const parsed = JSON.parse(xhr.responseText) as { error?: string; code?: string };
                  if (parsed.error) message = parsed.error;
                  code = parsed.code;
                } catch { /* ignore */ }
                if (xhr.status === 429 && code === "USAGE_LIMIT_EXCEEDED") resolve({ kind: "upgrade" });
                else resolve({ kind: "permanent", error: new Error(message) });
              }
            });
            xhr.addEventListener("error", () =>
              resolve({ kind: "retryable", error: new Error("Network error. Please check your connection and try again.") }),
            );

            xhr.open("POST", `${basePath}/api/jobs`);
            if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            xhr.send(form);
          });

          if (outcome.kind === "success") {
            result = outcome.data;
            lastErr = null;
            setRetryMessage(null);
            break;
          } else if (outcome.kind === "upgrade") {
            setShowUpgradePrompt(true);
            result = null;
            lastErr = null;
            setRetryMessage(null);
            break;
          } else if (outcome.kind === "permanent") {
            lastErr = outcome.error;
            break;
          } else {
            lastErr = outcome.error;
          }
        }

        if (lastErr) throw lastErr;
        return result;
      };

      let firstJobId: number | null = null;

      if (file.size <= DIRECT_UPLOAD_THRESHOLD) {
        // Small file — one POST per mode (files are ≤10 MB; re-sending is acceptable)
        for (let i = 0; i < modesArr.length; i++) {
          if (modesArr.length > 1) {
            setRetryMessage(`Uploading mode ${i + 1} of ${modesArr.length}…`);
          }
          const resp = await uploadSmallFile(modesArr[i]);
          if (resp && firstJobId === null) firstJobId = resp.id;
        }
        setRetryMessage(null);
      } else {
        // Large file — upload chunks once, then finalize for all modes in one request
        const { uploadId, totalChunks } = await uploadChunksOnly(file, basePath, token);
        const finalResult = await finalizeChunkedUpload(file, basePath, token, uploadId, totalChunks, modesArr, fullRecapPercent);
        if (finalResult) {
          if ("ids" in finalResult) {
            firstJobId = finalResult.ids[0] ?? null;
          } else {
            firstJobId = finalResult.id;
          }
        }
      }

      setIsUploading(false);
      queryClient.invalidateQueries({ queryKey: getListJobsQueryKey() });
      if (firstJobId !== null) {
        trackEvent("job_created", { mode: modesArr.join(","), source: "upload", job_count: modesArr.length });
        // Single mode: go directly to the job status page for a tighter feedback loop.
        // Multiple modes: go to job history so all new jobs are visible together.
        setLocation(modesArr.length === 1 ? `/jobs/${firstJobId}` : "/jobs");
      }
    } catch (err) {
      setIsUploading(false);
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "There was an error uploading your file.",
        variant: "destructive",
      });
    }
  };

  const handleFetchRss = async () => {
    if (!rssUrl.trim()) return;
    setRssFetching(true);
    setRssError(null);
    setRssEpisodes(null);
    setSelectedEpisodeGuids(new Set());
    setSubscribeSelectedUrls(new Set());
    setSubscribed(false);
    setRssFeedTitle(null);
    try {
      const result = await getRssEpisodes({ url: rssUrl.trim() });
      setRssEpisodes(result.episodes);
      if ("feedTitle" in result && result.feedTitle) {
        setRssFeedTitle(result.feedTitle as string);
      }
      addRecentFeed(rssUrl.trim());
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "error" in err
          ? String((err as { error: string }).error)
          : "Failed to fetch RSS feed. Check the URL and try again.";
      setRssError(msg);
    } finally {
      setRssFetching(false);
    }
  };

  const MAX_SUBSCRIBE_BACKFILL = 20;

  const handleSubscribe = async () => {
    if (!rssUrl.trim()) return;
    if (!isSignedIn) { setLocation("/sign-up"); return; }
    if (userPlan === null) return;
    if (userPlan === "free") { setLocation("/billing"); return; }
    setIsSubscribing(true);

    const modesArr = [...modes];
    const createdSubIds: number[] = [];
    let allConflict = true;
    let proRequired = false;

    for (const m of modesArr) {
      try {
        const result = await createSubscription({
          feedUrl: rssUrl.trim(),
          feedTitle: rssFeedTitle ?? undefined,
          mode: m,
          targetPercent: m === "full_recap" ? fullRecapPercent : undefined,
        });
        createdSubIds.push(result.subscription.id);
        allConflict = false;
      } catch (err: unknown) {
        const status = err && typeof err === "object" && "status" in err ? (err as { status: number }).status : null;
        if (status === 409) {
          // this mode already subscribed — skip silently
        } else if (status === 403) {
          proRequired = true;
          break;
        } else {
          toast({ title: "Subscribe failed", description: "Could not create subscription. Please try again.", variant: "destructive" });
          setIsSubscribing(false);
          return;
        }
      }
    }

    if (proRequired) {
      toast({ title: "Pro plan required", description: "RSS auto-processing is a Pro feature. Upgrade to unlock it.", variant: "destructive" });
      setLocation("/billing");
      setIsSubscribing(false);
      return;
    }

    if (allConflict && createdSubIds.length === 0) {
      setSubscribed(true);
      toast({ title: "Already subscribed", description: "You already have active subscriptions to this feed for the selected modes." });
      setIsSubscribing(false);
      return;
    }

    const firstSubId = createdSubIds[0] ?? null;

    if (firstSubId !== null) {
      trackEvent("subscription_created", { mode: modesArr.join(","), feed_url: rssUrl.trim() });
      const urlsToBackfill = Array.from(subscribeSelectedUrls).slice(0, MAX_SUBSCRIBE_BACKFILL);
      if (urlsToBackfill.length > 0) {
        let backfillFailed = false;
        // Backfill each newly created subscription so every mode gets catch-up jobs
        await Promise.all(
          createdSubIds.map((subId) =>
            backfillSubscriptionByUrls(subId, urlsToBackfill).catch(() => {
              backfillFailed = true;
            }),
          ),
        );
        if (backfillFailed) {
          toast({
            title: "Subscribed!",
            description: "Subscription active. Some catch-up episodes couldn't be queued — you can retry from the Feeds page.",
            variant: "default",
          });
        } else {
          toast({
            title: "Subscribed!",
            description: `Queuing ${urlsToBackfill.length} selected episode${urlsToBackfill.length !== 1 ? "s" : ""}${createdSubIds.length > 1 ? ` × ${createdSubIds.length} modes` : ""} now. New episodes will be abbreviated automatically as they publish.`,
          });
        }
        setIsSubscribing(false);
        setLocation(`/jobs?subscriptionId=${firstSubId}`);
        return;
      } else {
        setSubscribed(true);
        toast({
          title: "Subscribed!",
          description: "New episodes will be automatically abbreviated as they publish.",
        });
      }
    }
    setIsSubscribing(false);
  };

  const toggleSubscribeEpisode = (audioUrl: string) => {
    setSubscribeSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(audioUrl)) {
        next.delete(audioUrl);
      } else if (next.size < MAX_SUBSCRIBE_BACKFILL) {
        next.add(audioUrl);
      }
      return next;
    });
  };

  const toggleAllSubscribeEpisodes = () => {
    if (!rssEpisodes) return;
    const selectableUrls = rssEpisodes
      .filter((e) => !e.alreadyProcessed)
      .map((e) => e.audioUrl);
    const cappedUrls = selectableUrls.slice(0, MAX_SUBSCRIBE_BACKFILL);
    if (subscribeSelectedUrls.size >= cappedUrls.length && cappedUrls.every((u) => subscribeSelectedUrls.has(u))) {
      setSubscribeSelectedUrls(new Set());
    } else {
      setSubscribeSelectedUrls(new Set(cappedUrls));
    }
  };

  const toggleEpisode = (guid: string) => {
    setSelectedEpisodeGuids((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  const toggleAllEpisodes = () => {
    if (!rssEpisodes) return;
    const selectableGuids = rssEpisodes.filter((e) => !e.alreadyProcessed).map((e) => e.guid);
    if (selectableGuids.length > 0 && selectableGuids.every((g) => selectedEpisodeGuids.has(g))) {
      setSelectedEpisodeGuids(new Set());
    } else {
      setSelectedEpisodeGuids(new Set(selectableGuids));
    }
  };

  const handleRssSubmit = async () => {
    if (!rssEpisodes || selectedEpisodeGuids.size === 0) return;
    if (!isSignedIn) { setLocation("/sign-up"); return; }
    setIsSubmittingRss(true);

    const selectedEpisodes = rssEpisodes.filter((e) => selectedEpisodeGuids.has(e.guid));
    const modesArr = [...modes];
    const jobs: { id: number; status: string }[] = [];
    let firstJobId: number | null = null;
    let hasBlocked = false;

    for (const episode of selectedEpisodes) {
      for (const m of modesArr) {
        try {
          const job = await createRssJob({
            episodeUrl: episode.audioUrl,
            episodeTitle: episode.title,
            artworkUrl: episode.artworkUrl ?? undefined,
            mode: m,
            targetPercent: m === "full_recap" ? fullRecapPercent : undefined,
            fastMode: fastMode || undefined,
          });
          jobs.push({ id: job.id, status: job.status });
          if (firstJobId === null) firstJobId = job.id;
          if (job.status === "blocked") hasBlocked = true;
        } catch {
          toast({
            title: "Submission error",
            description: `Failed to queue episode "${episode.title}". Please try again.`,
            variant: "destructive",
          });
        }
      }
    }

    queryClient.invalidateQueries({ queryKey: getListJobsQueryKey() });
    setIsSubmittingRss(false);

    if (jobs.length === 0) return;
    trackEvent("job_created", { mode: modesArr.join(","), source: "rss", job_count: jobs.length });

    const episodeCount = selectedEpisodes.length;
    const totalJobs = jobs.length;

    if (hasBlocked && totalJobs === 1) {
      setShowUpgradePrompt(true);
    } else if (hasBlocked) {
      toast({
        title: "Some episodes blocked",
        description: `${jobs.filter((j) => j.status !== "blocked").length} job(s) queued. The rest require a Pro plan.`,
      });
      setLocation("/jobs");
    } else if (episodeCount === 1 && modesArr.length === 1 && firstJobId) {
      setLocation(`/jobs/${firstJobId}`);
    } else {
      toast({
        title: "Episodes queued",
        description: modesArr.length > 1
          ? `${episodeCount} episode${episodeCount !== 1 ? "s" : ""} queued with ${modesArr.length} modes each (${totalJobs} jobs total).`
          : `${episodeCount} episode${episodeCount !== 1 ? "s" : ""} queued for processing.`,
      });
      setLocation("/jobs");
    }
  };

  const quickEst = estimatedOutput(20);
  const quotesEst = estimatedOutput(15);
  const recapEst = estimatedOutput(fullRecapPercent);

  const selectedEpisodeCount = selectedEpisodeGuids.size;
  const quotaRemaining = userPlan === "pro" ? Infinity : userPlan === "free" ? Math.max(0, FREE_LIMIT - (userUsageCount ?? 0)) : null;
  const quotaExceeded = quotaRemaining !== null && quotaRemaining !== Infinity && selectedEpisodeCount > quotaRemaining;
  const isSubmitDisabled =
    isSubmittingRss ||
    selectedEpisodeCount === 0 ||
    !rssEpisodes;

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-mono font-bold tracking-tight">New Job</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Upload an audio file or paste a podcast RSS feed URL to begin abbreviation.
        </p>
      </div>

      {/* Upgrade prompt — shown when free plan limit is hit */}
      {showUpgradePrompt && (
        <Card className="border-2 border-indigo-500/60 bg-indigo-950/30">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <p className="font-bold text-foreground text-base">You've reached the free plan limit</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Free accounts get 5 abbreviations per month. Upgrade to Pro for unlimited access.
                </p>
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {[
                  "Unlimited abbreviations, every month",
                  "Process episodes up to 2 hours",
                  "All 3 abbreviation modes",
                  "Full job history",
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <Check size={14} className="text-indigo-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3">
                <Button
                  className="bg-indigo-500 hover:bg-indigo-600 flex-1"
                  onClick={() => setLocation("/billing")}
                >
                  Upgrade to Pro — $19/month
                  <ArrowRight size={15} className="ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUpgradePrompt(false)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        <Card className="border-2 border-border/50 bg-card/50 backdrop-blur-sm">
          <CardHeader className="pb-4 border-b border-border/10">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Activity size={16} className="text-primary" />
              Pipeline Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-8">

            {/* Input Mode Toggle */}
            <div className="space-y-3">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Source Type
              </Label>
              <div className="flex gap-2">
                <button
                  data-testid="tab-file-upload"
                  onClick={() => setInputMode("file")}
                  className={[
                    "flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-mono font-medium transition-all",
                    inputMode === "file"
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/50 text-muted-foreground hover:border-primary/40 hover:bg-secondary/20",
                  ].join(" ")}
                >
                  <UploadIcon size={15} />
                  Upload File
                </button>
                <button
                  data-testid="tab-rss-feed"
                  onClick={() => {
                    setInputMode("rss");
                    if (rssUrl.trim() && !rssFetching && rssEpisodes === null) {
                      handleFetchRss();
                    }
                  }}
                  className={[
                    "flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-mono font-medium transition-all",
                    inputMode === "rss"
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/50 text-muted-foreground hover:border-primary/40 hover:bg-secondary/20",
                  ].join(" ")}
                >
                  <Rss size={15} />
                  RSS Feed
                </button>
              </div>
            </div>

            {inputMode === "file" ? (
              /* File Drop Zone */
              <div className="space-y-3">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Source Audio
                </Label>
                <div
                  data-testid="upload-dropzone"
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={[
                    "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200",
                    isUploading ? "opacity-50 pointer-events-none" : "hover:border-primary/50 hover:bg-secondary/30",
                    isDragging ? "border-primary bg-primary/10 scale-[1.01]" : file ? "border-primary/30 bg-primary/5" : "border-border",
                  ].join(" ")}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="audio/*"
                    data-testid="input-audio-file"
                    onChange={handleFileChange}
                  />

                  {file ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <FileAudio className="h-6 w-6 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-mono text-sm font-medium" data-testid="text-selected-filename">
                          {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                          {fileDurationSec ? ` · ${formatMinutes(fileDurationSec)}` : ""}
                        </p>
                      </div>
                      {!isUploading && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 text-xs"
                          data-testid="button-change-file"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = "";
                          }}
                        >
                          Change File
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
                        <UploadIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {isDragging ? "Drop audio file here" : "Click to browse or drag and drop"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          MP3, WAV, FLAC, M4A, AAC, OGG — up to 200 MB, max 60 min
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* RSS Feed Input */
              <div className="space-y-4">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  RSS Feed URL
                </Label>
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        data-testid="input-rss-url"
                        placeholder="https://feeds.example.com/podcast.rss"
                        value={rssUrl}
                        onChange={(e) => { setRssUrl(e.target.value); setShowRecentFeeds(false); }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleFetchRss(); }}
                        className="font-mono text-sm pr-8"
                        disabled={rssFetching}
                      />
                      {recentFeeds.length > 0 && (
                        <button
                          type="button"
                          data-testid="button-recent-feeds-toggle"
                          onClick={() => setShowRecentFeeds((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label="Show recent feeds"
                          aria-expanded={showRecentFeeds}
                        >
                          <ChevronDown size={14} className={showRecentFeeds ? "rotate-180 transition-transform" : "transition-transform"} />
                        </button>
                      )}
                    </div>
                    <Button
                      data-testid="button-fetch-episodes"
                      onClick={handleFetchRss}
                      disabled={rssFetching || !rssUrl.trim()}
                      variant="outline"
                      className="shrink-0"
                    >
                      {rssFetching ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Search size={16} />
                      )}
                      <span className="ml-2">{rssFetching ? "Fetching…" : "Fetch"}</span>
                    </Button>
                  </div>
                  {showRecentFeeds && recentFeeds.length > 0 && (
                    <div
                      data-testid="recent-feeds-dropdown"
                      className="rounded-lg border border-border/60 bg-popover shadow-md divide-y divide-border/30 overflow-hidden"
                    >
                      <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-secondary/30">
                        Recent Feeds
                      </div>
                      {recentFeeds.map((url, idx) => (
                        <div
                          key={url}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-secondary/40 group"
                        >
                          <button
                            type="button"
                            data-testid={`recent-feed-item-${idx}`}
                            className="flex-1 text-left font-mono text-xs truncate text-foreground/80 hover:text-foreground"
                            onClick={() => {
                              setRssUrl(url);
                              setShowRecentFeeds(false);
                            }}
                          >
                            {url}
                          </button>
                          <button
                            type="button"
                            data-testid={`remove-recent-feed-${idx}`}
                            aria-label={`Remove ${url} from recent feeds`}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeRecentFeed(url);
                              if (recentFeeds.length <= 1) setShowRecentFeeds(false);
                            }}
                            className="shrink-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {rssError && (
                  <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    <AlertCircle size={15} className="mt-0.5 shrink-0" />
                    <span>{rssError}</span>
                  </div>
                )}

                {rssEpisodes && rssEpisodes.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                        {(() => {
                          const total = rssEpisodes.length;
                          const processed = rssEpisodes.filter((e) => e.alreadyProcessed).length;
                          const newCount = total - processed;
                          if (processed === 0) {
                            return `${total} Episode${total !== 1 ? "s" : ""} Found`;
                          } else if (newCount === 0) {
                            return `${total} Episode${total !== 1 ? "s" : ""} Found — all already processed`;
                          } else {
                            return `${total} Episode${total !== 1 ? "s" : ""} Found (${newCount} new)`;
                          }
                        })()}
                      </span>
                      <button
                        data-testid="button-select-all"
                        onClick={toggleAllEpisodes}
                        className="text-xs text-primary hover:underline font-mono"
                      >
                        {rssEpisodes.filter((e) => !e.alreadyProcessed).every((e) => selectedEpisodeGuids.has(e.guid)) && selectedEpisodeGuids.size > 0 ? "Deselect all" : "Select all"}
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto rounded-lg border border-border/50 divide-y divide-border/30">
                      {rssEpisodes.map((episode) => {
                        const isSelected = selectedEpisodeGuids.has(episode.guid);
                        const alreadyProcessed = episode.alreadyProcessed;
                        return (
                          <button
                            key={episode.guid}
                            data-testid={`episode-row-${episode.guid}`}
                            onClick={() => toggleEpisode(episode.guid)}
                            className={[
                              "w-full flex items-start gap-3 px-3 py-3 text-left transition-colors",
                              isSelected
                                ? "bg-primary/8"
                                : "hover:bg-secondary/30",
                            ].join(" ")}
                          >
                            <div className="mt-0.5 shrink-0 text-primary">
                              {isSelected
                                ? <CheckSquare size={16} />
                                : alreadyProcessed
                                  ? <CheckCircle2 size={16} className="text-green-500" />
                                  : <Square size={16} className="text-border" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium leading-snug line-clamp-2">{episode.title}</p>
                              <div className="flex items-center gap-3 mt-1">
                                {episode.pubDate && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                                    <Calendar size={10} />
                                    {formatPubDate(episode.pubDate)}
                                  </span>
                                )}
                                {episode.durationSeconds != null && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                                    <Clock size={10} />
                                    {formatMinutes(episode.durationSeconds)}
                                  </span>
                                )}
                                {alreadyProcessed ? (
                                  <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400 font-mono">
                                    <CheckCircle2 size={10} />
                                    processed
                                  </span>
                                ) : episode.durationSeconds != null && (
                                  <span className="text-xs text-muted-foreground font-mono">
                                    → est.{" "}
                                    <span className="text-primary">
                                      {modes.size === 1
                                        ? estimatedOutputFromDuration(
                                            episode.durationSeconds,
                                            modes.has("quick_catchup") ? 20 : modes.has("key_quotes") ? 15 : fullRecapPercent,
                                          )
                                        : (["key_quotes", "quick_catchup", "full_recap"] as Mode[])
                                            .filter((m) => modes.has(m))
                                            .map((m) => {
                                              const pct = m === "quick_catchup" ? 20 : m === "key_quotes" ? 15 : fullRecapPercent;
                                              const label = m === "key_quotes" ? "KQ" : m === "quick_catchup" ? "QC" : "FR";
                                              return `${label}: ${estimatedOutputFromDuration(episode.durationSeconds!, pct)}`;
                                            })
                                            .join(" · ")}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      {selectedEpisodeCount > 0 && (
                        <p className="text-xs text-muted-foreground font-mono">
                          {selectedEpisodeCount} episode{selectedEpisodeCount !== 1 ? "s" : ""} selected
                        </p>
                      )}
                      {userPlan === "free" && quotaRemaining !== null && quotaRemaining !== Infinity && (
                        <p className={["text-xs font-mono", quotaExceeded ? "text-amber-500" : "text-muted-foreground"].join(" ")}>
                          {quotaRemaining} of {FREE_LIMIT} free slots remaining this month
                        </p>
                      )}
                      {userPlan === "pro" && userAudioSecondsUsed !== null && (
                        <div className="flex flex-col gap-1 min-w-[140px]">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Audio quota</span>
                            <span className={["text-[10px] font-mono", userAudioSecondsUsed >= PRO_AUDIO_SECONDS_LIMIT * 0.9 ? "text-amber-500" : "text-muted-foreground"].join(" ")}>
                              {Math.round(userAudioSecondsUsed / 3600 * 10) / 10}h / {PRO_AUDIO_SECONDS_LIMIT / 3600}h
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-secondary overflow-hidden w-36">
                            <div
                              className={["h-full rounded-full transition-all", userAudioSecondsUsed >= PRO_AUDIO_SECONDS_LIMIT * 0.9 ? "bg-amber-500" : "bg-primary"].join(" ")}
                              style={{ width: `${Math.min(100, (userAudioSecondsUsed / PRO_AUDIO_SECONDS_LIMIT) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Subscribe to feed */}
                    <div className="rounded-lg border border-border/50 bg-secondary/20 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                            Auto-process new episodes
                            {userPlan === "free" && (
                              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/20">Pro</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {userPlan === "free"
                              ? "Upgrade to Pro to subscribe and get new episodes abbreviated automatically."
                              : "Subscribe to get new episodes abbreviated automatically as they publish."}
                          </p>
                        </div>
                        {subscribed ? (
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="flex items-center gap-1 text-xs text-green-500 font-mono">
                              <Check size={13} /> Subscribed
                            </span>
                            <button
                              onClick={() => setLocation("/subscriptions")}
                              className="text-xs text-primary hover:underline font-mono flex items-center gap-1"
                            >
                              Manage <ExternalLink size={11} />
                            </button>
                          </div>
                        ) : userPlan === "free" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-xs h-8 border-primary/40 text-primary hover:bg-primary/10"
                            onClick={() => setLocation("/billing")}
                            data-testid="button-subscribe-upgrade"
                          >
                            Upgrade to Pro
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-xs h-8"
                            disabled={isSubscribing || !rssUrl.trim() || userPlan === null}
                            onClick={handleSubscribe}
                            data-testid="button-subscribe-feed"
                          >
                            {isSubscribing ? (
                              <Loader2 size={13} className="animate-spin mr-1.5" />
                            ) : (
                              <BellPlus size={13} className="mr-1.5" />
                            )}
                            {isSubscribing ? "Subscribing…" : "Subscribe"}
                          </Button>
                        )}
                      </div>
                      {/* Catch-up episode picker — only shown for Pro users before subscribing */}
                      {userPlan === "pro" && !subscribed && rssEpisodes && rssEpisodes.length > 0 && (
                        <div className="border-t border-border/30 pt-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                              <Calendar size={12} className="shrink-0" />
                              Also catch up on past episodes
                            </p>
                            {(() => {
                              const selectable = rssEpisodes.filter((e) => !e.alreadyProcessed);
                              if (selectable.length === 0) return null;
                              const cappedSelectable = selectable.slice(0, MAX_SUBSCRIBE_BACKFILL);
                              const allCappedSelected = cappedSelectable.every((e) => subscribeSelectedUrls.has(e.audioUrl));
                              return (
                                <button
                                  data-testid="button-subscribe-select-all"
                                  onClick={toggleAllSubscribeEpisodes}
                                  className="text-xs text-primary hover:underline font-mono"
                                >
                                  {allCappedSelected ? "Deselect all" : `Select first ${Math.min(selectable.length, MAX_SUBSCRIBE_BACKFILL)}`}
                                </button>
                              );
                            })()}
                          </div>
                          {rssEpisodes.length > MAX_SUBSCRIBE_BACKFILL && (
                            <p className="text-[10px] text-muted-foreground font-mono flex items-center gap-1">
                              <Info size={10} className="shrink-0" />
                              Up to {MAX_SUBSCRIBE_BACKFILL} episodes can be queued for catch-up at once
                            </p>
                          )}
                          <div className="max-h-48 overflow-y-auto rounded-md border border-border/40 divide-y divide-border/20">
                            {rssEpisodes.map((episode) => {
                              const isSelected = subscribeSelectedUrls.has(episode.audioUrl);
                              const atCap = subscribeSelectedUrls.size >= MAX_SUBSCRIBE_BACKFILL && !isSelected;
                              const alreadyProcessed = episode.alreadyProcessed;
                              return (
                                <button
                                  key={episode.guid}
                                  data-testid={`subscribe-episode-row-${episode.guid}`}
                                  onClick={() => toggleSubscribeEpisode(episode.audioUrl)}
                                  disabled={atCap}
                                  className={[
                                    "w-full flex items-start gap-2.5 px-2.5 py-2 text-left transition-colors",
                                    isSelected
                                      ? "bg-primary/8"
                                      : atCap
                                        ? "opacity-40 cursor-not-allowed"
                                        : "hover:bg-secondary/30",
                                  ].join(" ")}
                                >
                                  <div className="mt-0.5 shrink-0 text-primary">
                                    {isSelected
                                      ? <CheckSquare size={14} />
                                      : alreadyProcessed
                                        ? <CheckCircle2 size={14} className="text-green-500" />
                                        : <Square size={14} className="text-border" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium leading-snug line-clamp-2">{episode.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {episode.pubDate && (
                                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
                                          <Calendar size={9} />
                                          {formatPubDate(episode.pubDate)}
                                        </span>
                                      )}
                                      {episode.durationSeconds != null && (
                                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
                                          <Clock size={9} />
                                          {formatMinutes(episode.durationSeconds)}
                                        </span>
                                      )}
                                      {alreadyProcessed && (
                                        <span className="flex items-center gap-0.5 text-[10px] text-green-600 dark:text-green-400 font-mono">
                                          <CheckCircle2 size={9} />
                                          processed
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {subscribeSelectedUrls.size > 0 && (
                            <p className="text-xs text-muted-foreground font-mono">
                              {subscribeSelectedUrls.size} of {MAX_SUBSCRIBE_BACKFILL} episode{subscribeSelectedUrls.size !== 1 ? "s" : ""} selected for catch-up
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mode Selection */}
            <div className="space-y-3">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <LayoutList size={14} /> Abbreviation Mode
                <span className="text-[10px] normal-case tracking-normal opacity-60">(select one or more)</span>
              </Label>
              <div className="space-y-3 pt-1">
                <ModeCard
                  id="key_quotes"
                  label="Key Quotes"
                  description="Extracts standalone, shareable moments — each clip makes sense out of context. Perfect for social media snippets."
                  icon={<Quote size={18} />}
                  targetLabel="~10–20% of original"
                  estimatedOutput={inputMode === "file" ? quotesEst : null}
                  featured
                  selected={modes.has("key_quotes")}
                  onSelect={() => toggleMode("key_quotes")}
                />

                <ModeCard
                  id="quick_catchup"
                  label="Quick Catch-Up"
                  description="Creates a coherent narrative recap of the episode. Jump to the core story in a fraction of the time."
                  icon={<Zap size={18} />}
                  targetLabel="~15–25% of original"
                  estimatedOutput={inputMode === "file" ? quickEst : null}
                  selected={modes.has("quick_catchup")}
                  onSelect={() => toggleMode("quick_catchup")}
                />

                <ModeCard
                  id="full_recap"
                  label="Full Recap"
                  description="You control the compression. Preserves the full narrative arc and editorial flow at your chosen density."
                  icon={<LayoutList size={18} />}
                  targetLabel={modes.has("full_recap") ? `${fullRecapPercent}% selected` : "30–70% · you choose"}
                  estimatedOutput={inputMode === "file" ? recapEst : null}
                  selected={modes.has("full_recap")}
                  onSelect={() => toggleMode("full_recap")}
                >
                  {modes.has("full_recap") && (
                    <div
                      className="mt-4 space-y-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-muted-foreground">Compression</span>
                        <span
                          className="font-mono text-sm font-bold text-primary"
                          data-testid="text-target-percent"
                        >
                          {fullRecapPercent}%
                        </span>
                      </div>
                      <Slider
                        disabled={isUploading || isSubmittingRss}
                        value={[fullRecapPercent]}
                        onValueChange={(val) => setFullRecapPercent(val[0])}
                        max={70}
                        min={30}
                        step={1}
                        className="w-full"
                        data-testid="slider-target-percent"
                      />
                      <div className="flex justify-between text-xs font-mono text-muted-foreground">
                        <span>Aggressive (30%)</span>
                        <span>Conservative (70%)</span>
                      </div>
                    </div>
                  )}
                </ModeCard>
              </div>
            </div>

            {/* Fast Mode toggle */}
            <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="fast-mode-toggle" className="text-sm font-medium cursor-pointer">
                  Fast Mode
                </Label>
                <p className="text-xs text-muted-foreground">
                  Uses a quicker transcription model — finishes ~2-3× faster with slightly lower accuracy
                </p>
              </div>
              <Switch
                id="fast-mode-toggle"
                checked={fastMode}
                onCheckedChange={setFastMode}
                disabled={isUploading || isSubmittingRss}
              />
            </div>

            {/* Upload Progress (file mode only) */}
            {inputMode === "file" && isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-mono">
                  <span>Uploading to ingest server...</span>
                  <span data-testid="text-upload-progress">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
                {retryMessage && (
                  <p
                    data-testid="text-upload-retry-message"
                    className="text-xs text-amber-500 font-mono"
                  >
                    {retryMessage}
                  </p>
                )}
              </div>
            )}

            {inputMode === "file" ? (
              <Button
                onClick={handleUpload}
                disabled={!file || isUploading}
                className="w-full h-12 font-mono uppercase tracking-wider font-bold"
                size="lg"
                data-testid="button-initialize-job"
              >
                {isUploading
                  ? "Uploading..."
                  : modes.size === 1
                    ? "Initialize Job"
                    : `Process ${modes.size} Ways`}
                {!isUploading && <Play className="ml-2 h-4 w-4" />}
              </Button>
            ) : (
              <div className="space-y-3">
                {quotaExceeded && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <Info size={15} className="mt-0.5 shrink-0 text-amber-500" />
                      <div>
                        <p className="text-sm font-medium text-amber-500">Free plan limit reached</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          You selected {selectedEpisodeCount} episode{selectedEpisodeCount !== 1 ? "s" : ""} but only have{" "}
                          {quotaRemaining} free slot{quotaRemaining !== 1 ? "s" : ""} remaining this month.
                          Deselect some episodes, or upgrade to Pro for unlimited processing.
                        </p>
                      </div>
                    </div>
                    <Button
                      className="w-full bg-indigo-500 hover:bg-indigo-600 font-mono text-xs uppercase tracking-wider"
                      onClick={() => setLocation("/billing")}
                    >
                      Upgrade to Pro — $19/month <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                )}
                <Button
                  onClick={handleRssSubmit}
                  disabled={isSubmitDisabled || quotaExceeded}
                  className="w-full h-12 font-mono uppercase tracking-wider font-bold"
                  size="lg"
                  data-testid="button-initialize-rss-jobs"
                >
                  {isSubmittingRss ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Queuing Episodes...
                    </>
                  ) : (
                    <>
                      {selectedEpisodeCount > 0
                        ? modes.size > 1
                          ? `Process ${selectedEpisodeCount} Episode${selectedEpisodeCount !== 1 ? "s" : ""} × ${modes.size} Modes`
                          : `Queue ${selectedEpisodeCount} Episode${selectedEpisodeCount !== 1 ? "s" : ""}`
                        : "Queue Episodes"}
                      <Play className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
